import {
  Button,
  Dialog,
  DialogBody,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  mergeClasses,
  Radio,
  RadioGroup,
  Spinner,
  Text,
  Textarea,
  Tooltip,
} from "@fluentui/react-components";
import {
  ArrowLeftRegular,
  ArrowRightRegular,
  CheckmarkRegular,
  Dismiss24Regular,
  ErrorCircle20Regular,
  Info16Regular,
  Warning20Regular,
} from "@fluentui/react-icons";
import { DialogFooterAction } from "components/dialogFooterAction/DialogFooterAction";
import {
  createWorkspace,
  getConversations,
  getUsers,
  sendCopyNotificationEmail,
} from "api";
import { ConstantValues } from "common/constants";
import { CustomOption } from "components/fields/peoplePicker/PeoplePicker.types";
import { CustomToggle } from "components/customToggle/CustomToggle";
import { PeoplePicker } from "components/fields/peoplePicker/PeoplePicker";
import { TagPickerCustom } from "components/fields/tagPicker/TagPicker";
import {
  deduplicateOptions,
  optionToUserDetails,
  validateDuplicateName,
  validateOwners,
  validateRequiredFields,
  WORKSPACE_FORM_LABELS as DIALOG_LABELS,
  WORKSPACE_ERROR_MESSAGES as ERROR_MESSAGES,
  WORKSPACE_FIELD_LIMITS as FIELD_LIMITS,
  WORKSPACE_PICKER_IDS as PICKER_IDS,
} from "utils/workspaceFormValidation";
import { CopyOption } from "models/enums/CopyType";
import { IConversation } from "models/IConversation";
import { ICopyOption } from "models/ICopyOption";
import { ICreateOrUpdateWorkspace, IWorkspace } from "models/IWorkspace";
import { IUserDetails } from "models/IUserDetails";
import { IUser } from "models/IUser";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import { showToast } from "../../../redux/toastActions";
import { setUsers } from "../../../redux/users";
import { addWorkspace } from "../../../redux/workspacesSlice";
import {
  useSharedBannerStyles,
  useSharedDialogFooterStyles,
} from "theme/sharedStyles";
import { isNullUndefinedOrEmpty } from "utils/generalUtils";
import { useCopyDialogStyles } from "./CopyOptionsDialog.styles";
import {
  ICopyOptionsDialogProps,
  ICopyResult,
} from "./CopyOptionsDialog.types";

const COPY_OPTION_DESCRIPTIONS: Record<string, string> = {
  [CopyOption.Users]: "Share with specific people",
  [CopyOption.Workspace]: "Visible to everyone in selected workspace(s)",
  [CopyOption.NewWorkspace]: "Copy chat into a brand-new workspace",
  [CopyOption.PersonalWorkspace]: "Save a private copy only visible to you",
};

const OPTIONS_WITH_DETAILS = [
  CopyOption.Users,
  CopyOption.Workspace,
  CopyOption.NewWorkspace,
];

const EMAIL_SUBJECT = "NH AIMS - You have a new shared chat";

export const CopyOptionsDialog = (
  props: ICopyOptionsDialogProps,
): JSX.Element => {
  const currentUser: IUser = useSelector(
    (state: RootState) => state.currentUserSlice.value,
  );
  const users: IUser[] = useSelector(
    (state: RootState) => state.usersSlice.value,
  );
  const isUsersLoading: boolean = useSelector(
    (state: RootState) => state.usersSlice.isLoading,
  );
  const workspaces: IWorkspace[] = useSelector(
    (state: RootState) => state.workspacesSlice.value,
  );
  const activeWorkspace = useSelector(
    (state: RootState) => state.activeWorkspaceSlice.value,
  );

  const dispatch = useDispatch();
  const localStyles = useCopyDialogStyles();
  const bannerStyles = useSharedBannerStyles();
  const footerStyles = useSharedDialogFooterStyles();

  const workspaceId = activeWorkspace?.id ?? ConstantValues.EMPTY_STRING;
  const { data: cachedConversations = [] } = useQuery(
    ["topBarConversations", workspaceId],
    async () => {
      if (!workspaceId) return [];
      return await getConversations({
        workspaceId,
        searchText: undefined,
        ascending: false,
        limit: 10,
        offset: 0,
      });
    },
    { enabled: Boolean(workspaceId) },
  );
  const resolvedTitle =
    props.conversation.title ||
    cachedConversations.find(
      (c: IConversation) => c.id === props.conversation.id,
    )?.title ||
    "Untitled";

  const [selectedCopyOption, setSelectedCopyOption] = useState<string>(
    ConstantValues.EMPTY_STRING,
  );
  const [step, setStep] = useState<1 | 2>(1);
  const [selectedUsers, setSelectedUsers] = useState<CustomOption[]>([]);
  const [selectedWorkspaces, setSelectedWorkspaces] = useState<CustomOption[]>(
    [],
  );
  const [errorMessage, setErrorMessage] = useState<string>(
    ConstantValues.EMPTY_STRING,
  );
  const [sendEmail, setSendEmail] = useState<boolean>(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState<string>(
    ConstantValues.EMPTY_STRING,
  );
  const [newWorkspaceDescription, setNewWorkspaceDescription] =
    useState<string>(ConstantValues.EMPTY_STRING);
  const [newWorkspaceOwners, setNewWorkspaceOwners] = useState<CustomOption[]>(
    [],
  );
  const [newWorkspaceMembers, setNewWorkspaceMembers] = useState<
    CustomOption[]
  >([]);
  const [newWorkspaceFieldErrors, setNewWorkspaceFieldErrors] = useState<
    Record<string, string>
  >({});
  const [isCreatingWorkspace, setIsCreatingWorkspace] =
    useState<boolean>(false);
  const errorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const defaultEmailBody = useMemo(
    () =>
      `${currentUser.displayName} is sharing a chat with you within NH AIMS titled "${resolvedTitle}".`,
    [currentUser.displayName, resolvedTitle],
  );

  const [emailBody, setEmailBody] = useState<string>(defaultEmailBody);

  useEffect(() => {
    if (!users.length && !isUsersLoading) {
      getUsers()
        .then((data: IUser[]) => dispatch(setUsers(data)))
        .catch(console.error);
    }
  }, [dispatch, users.length, isUsersLoading]);

  useEffect(() => {
    return () => {
      if (errorTimerRef.current) {
        clearTimeout(errorTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setEmailBody(defaultEmailBody);
  }, [defaultEmailBody]);

  useEffect(() => {
    if (step === 2 && selectedCopyOption === CopyOption.NewWorkspace) {
      setNewWorkspaceOwners([
        {
          id: currentUser.id,
          key: currentUser.id,
          text: currentUser.displayName,
        },
      ]);
    }
  }, [step, selectedCopyOption, currentUser]);

  const handleCopyOptionChange = (
    _: React.FormEvent,
    data: { value: string },
  ): void => {
    const newValue = data.value;
    if (newValue !== CopyOption.Users && newValue !== CopyOption.Workspace) {
      setSendEmail(false);
    }
    setSelectedCopyOption(newValue);
  };

  const handleSelectedUsers = (selectedOptions: CustomOption[]): void => {
    setSelectedUsers(selectedOptions);
  };

  const handleSelectedWorkspaces = (selectedOptions: CustomOption[]): void => {
    setSelectedWorkspaces([...selectedOptions]);
  };

  const resetNewWorkspaceForm = (): void => {
    setNewWorkspaceName(ConstantValues.EMPTY_STRING);
    setNewWorkspaceDescription(ConstantValues.EMPTY_STRING);
    setNewWorkspaceOwners([]);
    setNewWorkspaceMembers([]);
    setNewWorkspaceFieldErrors({});
    setIsCreatingWorkspace(false);
  };

  const resetToDefault = (): void => {
    setSelectedUsers([]);
    setSelectedWorkspaces([]);
    setSendEmail(false);
    setStep(1);
    setSelectedCopyOption(ConstantValues.EMPTY_STRING);
    setEmailBody(defaultEmailBody);
    resetNewWorkspaceForm();
  };

  const handleSendEmailChange = (checked: boolean): void => {
    setSendEmail(checked);
  };

  const setTransientError = (msg: string): void => {
    setErrorMessage(msg);
    if (errorTimerRef.current) {
      clearTimeout(errorTimerRef.current);
    }
    errorTimerRef.current = setTimeout(
      () => setErrorMessage(ConstantValues.EMPTY_STRING),
      3000,
    );
  };

  const getRecipientEmailsForSelectedUsers = (): string[] => {
    return selectedUsers.map((s) => {
      const matched = users.find(
        (u) => u.id === s.id || u.userPrincipalName === s.key,
      );
      return matched?.mail ?? s.secondaryText ?? s.key;
    });
  };

  const getRecipientEmailsForWorkspaceMembers = (): string[] => {
    const selectedWorkspaceIds = selectedWorkspaces.map(
      (w) => w.key ?? ConstantValues.EMPTY_STRING,
    );
    const memberEmails = new Set<string>();

    selectedWorkspaceIds.forEach((workspaceId) => {
      const workspace = workspaces.find((w) => w.id === workspaceId);
      if (workspace) {
        if (workspace.teamMembers && workspace.teamMembers.length > 0) {
          workspace.teamMembers.forEach((member) => {
            const user = users.find((u) => u.id === member.userId);
            if (user) {
              memberEmails.add(user.mail ?? user.userPrincipalName);
            }
          });
        }
        if (workspace.owners && workspace.owners.length > 0) {
          workspace.owners.forEach((owner) => {
            const user = users.find((u) => u.id === owner.userId);
            if (user) {
              memberEmails.add(user.mail ?? user.userPrincipalName);
            }
          });
        }
      }
    });

    return Array.from(memberEmails);
  };

  const handleNextOrShare = (): void => {
    if (!selectedCopyOption) {
      setTransientError("Please select an option");
      return;
    }

    if (
      OPTIONS_WITH_DETAILS.includes(selectedCopyOption as CopyOption) &&
      step === 1
    ) {
      setErrorMessage(ConstantValues.EMPTY_STRING);
      setStep(2);
      return;
    }

    onCopy();
  };

  const onCopy = async (): Promise<void> => {
    try {
      if (!selectedCopyOption) {
        setTransientError("Please select an option");
        return;
      }

      if (
        selectedCopyOption === CopyOption.PersonalWorkspace &&
        activeWorkspace?.name === ConstantValues.PERSONAL_WORKSPACE
      ) {
        setTransientError("You are already in your Personal Workspace");
        return;
      }

      if (selectedCopyOption === CopyOption.Users) {
        if (
          selectedUsers.some(
            (s) =>
              s.id === currentUser.id ||
              s.key === currentUser.userPrincipalName,
          )
        ) {
          setTransientError("You cannot share with yourself!");
          return;
        }
      }

      let selectedIds: string[] = [];
      if (selectedCopyOption === CopyOption.Users) {
        selectedIds = selectedUsers.map(
          (user: CustomOption): string =>
            user.id ?? ConstantValues.EMPTY_STRING,
        );
      } else if (selectedCopyOption === CopyOption.Workspace) {
        selectedIds = selectedWorkspaces.map(
          (workspace: CustomOption): string =>
            workspace.key ?? ConstantValues.EMPTY_STRING,
        );
      }

      const copyOption: ICopyOption = {
        copyOption: selectedCopyOption as CopyOption,
        copiedFromUser: {
          userId: props.conversation.userId,
          displayName: props.conversation.displayName,
        },
        copiedFromConversationId: props.conversation.id ?? "",
        selectedIds,
      };

      const shouldSendEmail = sendEmail;
      const capturedEmailBody = emailBody;
      const capturedCopyOption = selectedCopyOption;
      const capturedSelectedUsers = [...selectedUsers];

      const results: ICopyResult[] = (await props.onCopy(copyOption)) ?? [];
      dispatch(showToast("Chat shared successfully", "success"));
      resetToDefault();

      if (shouldSendEmail && results.length > 0) {
        const buildBody = (wsId: string, convId: string) => {
          const params = new URLSearchParams();
          if (wsId) params.set("workspaceId", wsId);
          if (convId) params.set("conversationId", convId);
          const chatUrl = `${window.location.origin}/?${params.toString()}`;
          return `${capturedEmailBody}\n\nTo view the conversation, please visit NH AIMS at ${chatUrl}`;
        };

        const onEmailError = (error: unknown) => {
          console.error("Failed to send copy notification email:", error);
          dispatch(
            showToast(
              "Chat shared, but the email notification failed to send.",
              "warning",
            ),
          );
        };

        if (capturedCopyOption === CopyOption.Users) {
          results.forEach((result) => {
            const user = users.find((u) => u.id === result.recipientId);
            const email =
              user?.mail ||
              user?.userPrincipalName ||
              capturedSelectedUsers.find((s) => s.id === result.recipientId)
                ?.secondaryText ||
              capturedSelectedUsers.find((s) => s.id === result.recipientId)
                ?.key;
            if (email) {
              sendCopyNotificationEmail({
                recipients: [email],
                subject: EMAIL_SUBJECT,
                body: buildBody(result.workspaceId, result.conversationId),
              }).catch(onEmailError);
            }
          });
        } else if (
          capturedCopyOption === CopyOption.Workspace ||
          capturedCopyOption === CopyOption.NewWorkspace
        ) {
          results.forEach((result) => {
            const workspace = workspaces.find(
              (w) => w.id === result.workspaceId,
            );
            const memberEmails = [
              ...(workspace?.teamMembers ?? []),
              ...(workspace?.owners ?? []),
            ].flatMap((member) => {
              const u = users.find((u) => u.id === member.userId);
              return u?.mail || u?.userPrincipalName
                ? [u.mail || u.userPrincipalName]
                : [];
            });
            const uniqueEmails = [...new Set(memberEmails)];
            if (uniqueEmails.length > 0) {
              sendCopyNotificationEmail({
                recipients: uniqueEmails,
                subject: EMAIL_SUBJECT,
                body: buildBody(result.workspaceId, result.conversationId),
              }).catch(onEmailError);
            }
          });
        }
      }
    } catch (error) {
      console.error("Failed to share chat:", error);
      setTransientError("An error occurred while sharing. Please try again.");
    }
  };

  const handleBack = (): void => {
    setStep(1);
    setSelectedUsers([]);
    setSelectedWorkspaces([]);
    setSendEmail(false);
    setEmailBody(defaultEmailBody);
    resetNewWorkspaceForm();
  };

  const handleCreateAndShare = async (): Promise<void> => {
    const errors: Record<string, string> = {};

    if (newWorkspaceName.length > FIELD_LIMITS.NAME_MAX_LENGTH) {
      errors.name = `${DIALOG_LABELS.WORKSPACE_NAME} must be ${FIELD_LIMITS.NAME_MAX_LENGTH} characters or fewer.`;
    }
    if (newWorkspaceDescription.length > FIELD_LIMITS.DESCRIPTION_MAX_LENGTH) {
      errors.clientName = `${DIALOG_LABELS.DESCRIPTION} must be ${FIELD_LIMITS.DESCRIPTION_MAX_LENGTH} characters or fewer.`;
    }

    Object.assign(
      errors,
      validateRequiredFields(newWorkspaceName, newWorkspaceDescription),
    );
    Object.assign(errors, validateDuplicateName(newWorkspaceName, workspaces));
    Object.assign(errors, validateOwners(newWorkspaceOwners));

    if (Object.keys(errors).length > 0) {
      setNewWorkspaceFieldErrors(errors);
      return;
    }

    setIsCreatingWorkspace(true);
    try {
      const ownerIds = new Set(newWorkspaceOwners.map((o) => o.id));
      const members: IUserDetails[] = newWorkspaceMembers
        .filter((member) => member.id && !ownerIds.has(member.id))
        .map(optionToUserDetails);

      const workspacePayload: ICreateOrUpdateWorkspace = {
        name: newWorkspaceName,
        clientName: newWorkspaceDescription,
        teamMembers: members,
        owners: newWorkspaceOwners.map(optionToUserDetails),
      };

      const resultWorkspace: IWorkspace = await createWorkspace({
        workspace: workspacePayload,
      });
      dispatch(addWorkspace(resultWorkspace));

      const copyOption: ICopyOption = {
        copyOption: CopyOption.NewWorkspace,
        copiedFromUser: {
          userId: props.conversation.userId,
          displayName: props.conversation.displayName,
        },
        copiedFromConversationId: props.conversation.id ?? "",
        selectedIds: [resultWorkspace.id],
      };

      props.onCopy(copyOption);
      resetToDefault();
    } catch (e) {
      console.error("Error creating workspace:", e);
      setNewWorkspaceFieldErrors({
        general: ERROR_MESSAGES.SAVE_ERROR,
      });
      setIsCreatingWorkspace(false);
    }
  };

  const handleNewWorkspaceNameChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setNewWorkspaceName(e.target.value);
    if (newWorkspaceFieldErrors.name) {
      setNewWorkspaceFieldErrors((prev) => ({ ...prev, name: "" }));
    }
  };

  const handleNewWorkspaceDescriptionChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ): void => {
    setNewWorkspaceDescription(e.target.value);
    if (newWorkspaceFieldErrors.clientName) {
      setNewWorkspaceFieldErrors((prev) => ({ ...prev, clientName: "" }));
    }
  };

  const handleNewWorkspaceOwnersChange = (selected: CustomOption[]): void => {
    setNewWorkspaceOwners(deduplicateOptions(selected));
    if (newWorkspaceFieldErrors.owners) {
      setNewWorkspaceFieldErrors((prev) => ({ ...prev, owners: "" }));
    }
  };

  const handleNewWorkspaceMembersChange = (selected: CustomOption[]): void => {
    setNewWorkspaceMembers(deduplicateOptions(selected));
    if (newWorkspaceFieldErrors.teamMembers) {
      setNewWorkspaceFieldErrors((prev) => ({ ...prev, teamMembers: "" }));
    }
  };

  const renderEmailSection = (): JSX.Element | null => {
    if (!sendEmail) return null;

    const recipientEmails =
      selectedCopyOption === CopyOption.Users
        ? getRecipientEmailsForSelectedUsers().join(", ")
        : selectedCopyOption === CopyOption.Workspace
          ? getRecipientEmailsForWorkspaceMembers().join(", ")
          : ConstantValues.EMPTY_STRING;

    return (
      <div className={localStyles.emailSectionDivider}>
        <div className={localStyles.emailFields}>
          <div>
            <span className={localStyles.emailFieldLabel}>To</span>
            <Field>
              <Tooltip
                content={recipientEmails || "No recipients selected"}
                relationship="description"
                positioning="above"
              >
                <Input
                  readOnly
                  value={recipientEmails}
                  appearance="filled-darker"
                />
              </Tooltip>
            </Field>
          </div>

          <div>
            <span className={localStyles.emailFieldLabel}>Subject</span>
            <Field>
              <Input
                readOnly
                value={EMAIL_SUBJECT}
                appearance="filled-darker"
              />
            </Field>
          </div>

          <div>
            <span className={localStyles.emailFieldLabel}>Message</span>
            <Field>
              <Textarea
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                placeholder={defaultEmailBody}
                resize="vertical"
              />
            </Field>
          </div>
        </div>
      </div>
    );
  };

  const renderStep1 = (): JSX.Element => (
    <>
      <DialogBody className={localStyles.dialogBody}>
        <div className={localStyles.rootColumn}>
          <div className={localStyles.warningBannerWrapper}>
            {errorMessage ? (
              <div
                className={bannerStyles.errorBanner}
                role="alert"
                aria-live="assertive"
              >
                <ErrorCircle20Regular aria-hidden="true" />
                <span className={bannerStyles.errorBannerText}>
                  {errorMessage}
                </span>
              </div>
            ) : (
              <div className={bannerStyles.warningBanner} role="alert">
                <Warning20Regular aria-hidden="true" />
                <span className={bannerStyles.warningBannerText}>
                  This creates a new instance of this chat and cannot be undone.
                </span>
              </div>
            )}
          </div>

          <div className={localStyles.copyOptionsRow}>
            <Text className={localStyles.shareWithLabel}>Share with</Text>

            <RadioGroup
              value={selectedCopyOption}
              onChange={handleCopyOptionChange}
              className={localStyles.radioGroup}
              aria-label="Share options"
            >
              {[
                CopyOption.Users,
                CopyOption.Workspace,
                CopyOption.NewWorkspace,
                CopyOption.PersonalWorkspace,
              ].map((option) => (
                <label
                  key={option}
                  className={mergeClasses(
                    localStyles.radioCard,
                    selectedCopyOption === option &&
                      localStyles.radioCardSelected,
                  )}
                >
                  <Radio value={option} />
                  <div className={localStyles.radioCardContent}>
                    <span className={localStyles.radioCardTitle}>{option}</span>
                    <span className={localStyles.radioCardDescription}>
                      {COPY_OPTION_DESCRIPTIONS[option]}
                    </span>
                  </div>
                </label>
              ))}
            </RadioGroup>
          </div>
        </div>
      </DialogBody>

      <div className={footerStyles.dialogFooter}>
        <DialogFooterAction
          icon={
            OPTIONS_WITH_DETAILS.includes(selectedCopyOption as CopyOption) ? (
              <ArrowRightRegular />
            ) : (
              <CheckmarkRegular />
            )
          }
          onClick={handleNextOrShare}
          disabled={!selectedCopyOption}
          dimmed={!selectedCopyOption}
          tooltip={
            OPTIONS_WITH_DETAILS.includes(selectedCopyOption as CopyOption)
              ? "Next"
              : "Share"
          }
        />
      </div>
    </>
  );

  const isStep2ActionDisabled =
    isCreatingWorkspace ||
    (selectedCopyOption === CopyOption.Users && selectedUsers.length === 0) ||
    (selectedCopyOption === CopyOption.Workspace &&
      selectedWorkspaces.length === 0) ||
    (selectedCopyOption === CopyOption.NewWorkspace &&
      (!newWorkspaceName.trim() ||
        !newWorkspaceDescription.trim() ||
        newWorkspaceOwners.length === 0));

  const renderStep2 = (): JSX.Element => (
    <>
      <DialogBody className={localStyles.dialogBody}>
        <div className={localStyles.rootColumn}>
          {errorMessage && (
            <div
              className={bannerStyles.errorBanner}
              role="alert"
              aria-live="assertive"
            >
              <ErrorCircle20Regular aria-hidden="true" />
              <span className={bannerStyles.errorBannerText}>
                {errorMessage}
              </span>
            </div>
          )}

          {selectedCopyOption === CopyOption.Users && (
            <>
              <div className={localStyles.sectionSpacing}>
                <Field label="Select Users" required>
                  <PeoplePicker
                    id="user"
                    selectedOptions={selectedUsers}
                    handleSelectedItems={handleSelectedUsers}
                    hasOnlyOurUsers
                    disabled={false}
                  />
                </Field>
              </div>

              <div className={localStyles.toggleWrapper}>
                <CustomToggle
                  checked={sendEmail}
                  onChange={handleSendEmailChange}
                  label="Send email notification"
                  hint="Recipients get an email with a link"
                  labelPosition="start"
                />
              </div>

              {renderEmailSection()}
            </>
          )}

          {selectedCopyOption === CopyOption.Workspace && (
            <>
              <div className={localStyles.workspacePickerWrapper}>
                <Field label="Select Workspaces" required>
                  <TagPickerCustom
                    items={workspaces
                      .filter(
                        (w: IWorkspace) =>
                          w.id !== activeWorkspace?.id &&
                          w.name !== ConstantValues.PERSONAL_WORKSPACE &&
                          !w.archived,
                      )
                      .sort((a: IWorkspace, b: IWorkspace) =>
                        a.name.localeCompare(b.name),
                      )
                      .map((w: IWorkspace): CustomOption => ({
                        key: w.id,
                        text: w.name,
                      }))}
                    selectedItems={selectedWorkspaces.filter(
                      (w) => !isNullUndefinedOrEmpty(w),
                    )}
                    onSelectionChange={handleSelectedWorkspaces}
                  />
                </Field>
              </div>

              <div className={localStyles.toggleWrapper}>
                <CustomToggle
                  checked={sendEmail}
                  onChange={handleSendEmailChange}
                  label="Send email notification"
                  hint="Recipients get an email with a link"
                  labelPosition="start"
                />
              </div>

              {renderEmailSection()}
            </>
          )}

          {selectedCopyOption === CopyOption.NewWorkspace && (
            <>
              {isCreatingWorkspace ? (
                <div className={localStyles.spinnerWrapper}>
                  <Spinner label="Creating workspace..." />
                </div>
              ) : (
                <>
                  {newWorkspaceFieldErrors.general && (
                    <div
                      className={bannerStyles.errorBanner}
                      role="alert"
                      aria-live="assertive"
                    >
                      <ErrorCircle20Regular aria-hidden="true" />
                      <span className={bannerStyles.errorBannerText}>
                        {newWorkspaceFieldErrors.general}
                      </span>
                    </div>
                  )}

                  <div
                    className={localStyles.inlineHintBanner}
                    role="note"
                    aria-live="polite"
                  >
                    <Info16Regular
                      className={localStyles.inlineHintIcon}
                      aria-hidden="true"
                    />
                    The chat will be copied into this new workspace.
                  </div>

                  <div className={localStyles.formField}>
                    <Field
                      label={DIALOG_LABELS.WORKSPACE_NAME}
                      required
                      validationState={
                        newWorkspaceFieldErrors.name ? "error" : "none"
                      }
                      validationMessage={newWorkspaceFieldErrors.name}
                    >
                      <Input
                        value={newWorkspaceName}
                        onChange={handleNewWorkspaceNameChange}
                        placeholder="Enter workspace name..."
                      />
                    </Field>
                  </div>

                  <div className={localStyles.formField}>
                    <Field
                      label={DIALOG_LABELS.DESCRIPTION}
                      required
                      validationState={
                        newWorkspaceFieldErrors.clientName ? "error" : "none"
                      }
                      validationMessage={newWorkspaceFieldErrors.clientName}
                    >
                      <Textarea
                        value={newWorkspaceDescription}
                        onChange={handleNewWorkspaceDescriptionChange}
                        placeholder="Enter workspace description..."
                        resize="vertical"
                        rows={6}
                      />
                    </Field>
                  </div>

                  <div className={localStyles.formField}>
                    <Field
                      label={DIALOG_LABELS.OWNERS}
                      required
                      validationState={
                        newWorkspaceFieldErrors.owners ? "error" : "none"
                      }
                      validationMessage={newWorkspaceFieldErrors.owners}
                    >
                      <PeoplePicker
                        id={PICKER_IDS.OWNERS}
                        selectedOptions={newWorkspaceOwners}
                        handleSelectedItems={handleNewWorkspaceOwnersChange}
                        hasOnlyOurUsers
                        disabled={false}
                      />
                    </Field>
                  </div>

                  <div className={localStyles.formFieldLast}>
                    <Field
                      label={DIALOG_LABELS.TEAM_MEMBERS}
                      validationState={
                        newWorkspaceFieldErrors.teamMembers ? "error" : "none"
                      }
                      validationMessage={newWorkspaceFieldErrors.teamMembers}
                    >
                      <PeoplePicker
                        id={PICKER_IDS.TEAM_MEMBERS}
                        selectedOptions={newWorkspaceMembers}
                        handleSelectedItems={handleNewWorkspaceMembersChange}
                        hasOnlyOurUsers
                        disabled={false}
                      />
                    </Field>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </DialogBody>

      <div className={footerStyles.dialogFooter}>
        <DialogFooterAction
          icon={<ArrowLeftRegular />}
          onClick={handleBack}
          tooltip="Back"
        />
        <DialogFooterAction
          icon={<CheckmarkRegular />}
          onClick={
            selectedCopyOption === CopyOption.NewWorkspace
              ? handleCreateAndShare
              : onCopy
          }
          disabled={isStep2ActionDisabled}
          dimmed={isStep2ActionDisabled}
          tooltip={
            selectedCopyOption === CopyOption.NewWorkspace
              ? "Create & Share"
              : "Share"
          }
        />
      </div>
    </>
  );

  return (
    <Dialog
      modalType="modal"
      open={props.isOpen}
      onOpenChange={(event, data) => {
        if (data.open === false && !isCreatingWorkspace) {
          resetToDefault();
          props.onClose();
        }
      }}
    >
      <DialogSurface
        className={mergeClasses(
          localStyles.dialogSurface,
          footerStyles.dialogSurface,
        )}
      >
        <div className={localStyles.dialogHeader}>
          <DialogTitle className={localStyles.dialogTitle}>
            Share Options
          </DialogTitle>
          <Button
            appearance="subtle"
            icon={<Dismiss24Regular />}
            aria-label="Close dialog"
            onClick={() => {
              resetToDefault();
              props.onClose();
            }}
            disabled={isCreatingWorkspace}
          />
        </div>

        <Text size={300} className={localStyles.subtitle}>
          {resolvedTitle}
        </Text>

        {step === 1 ? renderStep1() : renderStep2()}
      </DialogSurface>
    </Dialog>
  );
};
